'use client';

import { useMemo } from 'react';

import { intersection, isEmpty } from 'lodash';
import { Globe } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';

import { useConfigValue } from '@/components/providers/config-provider';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { locales } from '@/i18n/locales';
import { SYNC_EVENTS } from '@/lib/sync/events';
import { useCrossTabSync } from '@/lib/sync/hooks/use-cross-tab-sync';
import { changeLocale } from '@/lib/utils/locale';
import { getTextDirection } from '@/lib/utils/locale-shared';

export function LanguageSelector() {
  const t = useTranslations('headerActions.language');
  const locale = useLocale();
  const { emit } = useCrossTabSync();

  const enableLanguageSelector = useConfigValue<boolean>('meta.enableLanguageSelector') ?? false;
  const tenantLocales = useConfigValue<ReadonlyArray<string>>('meta.availableLocales');

  const availableLocales = useMemo(() => {
    if (isEmpty(tenantLocales)) {
      return locales;
    }
    return intersection(locales, tenantLocales?.map(l => l.toLowerCase()) ?? []);
  }, [tenantLocales]);

  const getLanguageName = useMemo(
    () => (code: string) => {
      try {
        const displayNames = new Intl.DisplayNames([code], { type: 'language' });
        return displayNames.of(code) ?? code.toUpperCase();
      } catch {
        return code.toUpperCase();
      }
    },
    [],
  );

  const handleLanguageChange = (newLocale: string) => {
    if (newLocale === locale) {
      return;
    }

    emit(SYNC_EVENTS.LOCALE, newLocale);
    changeLocale(newLocale);
  };

  if (!enableLanguageSelector || availableLocales.length <= 1) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-7 gap-1.5 px-2 text-xs font-medium"
          aria-label={t('label')}
        >
          <Globe className="size-4" />
          <span>{getLanguageName(locale)}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {availableLocales.map(loc => (
          <DropdownMenuItem
            key={loc}
            onClick={() => handleLanguageChange(loc)}
            className="gap-2"
            dir={getTextDirection(loc)}
          >
            <span className={locale === loc ? 'font-semibold' : ''}>{getLanguageName(loc)}</span>
            {locale === loc && <span className="ms-auto">✓</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
